// src/pages/admin/adminLogin.tsx
import React, { useState } from "react";
import {
    Box,
    Card,
    TextField,
    Typography,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Person } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAdminCredentials } from "../../features/admin/authSlice.ts";

const AdminLogin = () => {
    const [username, setUsername]       = useState("");
    const [password, setPassword]       = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errorMsg, setErrorMsg]       = useState("");
    const [successMsg, setSuccessMsg]   = useState("");
    const [loading, setLoading]         = useState(false);
    const navigate  = useNavigate();
    const dispatch  = useDispatch();

    const handleAdminLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!username.trim()) {
            setErrorMsg("Username is required");
            setLoading(false);
            return;
        }
        if (!password.trim()) {
            setErrorMsg("Password is required");
            setLoading(false);
            return;
        }

        const loginURL = `${process.env.REACT_APP_SERVER_ENDPOINT}/admin/login`;
        const authkey  = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";

        try {
            const response = await fetch(loginURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept":       "application/json",
                    "authkey":      authkey,
                },
                body: JSON.stringify({ username: username.trim(), password }),
            });

            if (!response.ok) {
                if      (response.status === 404) throw new Error("404 Not Found: The API endpoint was not found.");
                else if (response.status === 403) throw new Error("403 Forbidden: Invalid authkey or permissions.");
                else if (response.status === 401) throw new Error("401 Unauthorized: Invalid credentials.");
                else                              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("🔐 Login API full response:", JSON.stringify(result));

            if (!result.error && result.token) {
                setSuccessMsg("Login successful! Redirecting...");

                const token = result.token;

                // Clear any leftover vendor session first, so a stale
                // vendor_token/user_role never gets mixed with this admin
                // session (mirrors the cleanup done in vendorlogin.tsx).
                localStorage.removeItem("vendor_token");
                localStorage.removeItem("vendor_id");
                localStorage.removeItem("user_email");
                localStorage.removeItem("user_name");
                localStorage.removeItem("user_role");
                localStorage.removeItem("user_id");

                // ── Step 1: Save token ──
                localStorage.setItem("admin_token",    token);
                localStorage.setItem("admin_password", password); // for token refresh

                // ── Step 2: Get role from API response ──
                // API returns: { role: "Test", role_id: 21, permissions: { dashboard: ["view"], ... } }
                const apiRole   = result.role    || result.admin_type || "";
                const apiRoleId = result.role_id || result.id         || "";

                // ── Step 3: Decode JWT as fallback ──
                let jwtRole   = "";
                let jwtUserId = "";
                let jwtEmail  = "";

                const tokenParts = token.split(".");
                if (tokenParts.length === 3) {
                    try {
                        const payload = JSON.parse(atob(tokenParts[1]));
                        jwtRole   = (payload.role  || "").toLowerCase().trim();
                        jwtUserId = String(payload.id || "");
                        jwtEmail  = payload.email  || "";
                        console.log("🔓 JWT payload:", payload);
                    } catch (decodeError) {
                        console.warn("JWT decode failed:", decodeError);
                    }
                }

                // ── Step 4: Determine final role ──
                // Priority: API response role > JWT role > "admin"
                const rawRole   = (apiRole || jwtRole || "admin").toLowerCase().trim();
                const finalRole = normalizeRole(rawRole);

                const userId = String(apiRoleId || jwtUserId || "");
                const email  = jwtEmail || username;

                console.log("👤 rawRole:", rawRole, "→ finalRole:", finalRole);

                // ── Step 5: Save everything to localStorage ──
                localStorage.setItem("admin_type",  finalRole);
                localStorage.setItem("role",        rawRole);   // preserve original e.g. "Test"
                localStorage.setItem("admin_id",    userId);
                localStorage.setItem("admin_name",  username);
                localStorage.setItem("admin_email", email);

                if (apiRoleId) {
                    localStorage.setItem("admin_role_id", String(apiRoleId));
                }

                // ── KEY: Save permissions from login response ──
                // API returns: { permissions: { dashboard: ["view"], user_list: ["view"],
                //                return_process_management: ["view","add","edit","delete"] } }
                //
                // RULE enforced in SlideMenu:
                //   ["view"]                          → canView:true,  canWrite:false → 🔒 View badge, read-only
                //   ["view","add","edit","delete"]    → canView:true,  canWrite:true  → Full access
                //   not present in permissions        → canView:false                 → Menu hidden
                if (result.permissions && Object.keys(result.permissions).length > 0) {
                    localStorage.setItem("admin_permissions", JSON.stringify(result.permissions));
                    console.log("💾 Saved permissions:", result.permissions);
                } else {
                    // No permissions in response → clear any stale permissions
                    localStorage.removeItem("admin_permissions");
                }

                // Also clear any stale perm_map so SlideMenu rebuilds fresh
                localStorage.removeItem("admin_perm_map");

                // ── Step 6: Dispatch to Redux ──
                dispatch(setAdminCredentials({
                    token:     token,
                    email:     email,
                    userId:    userId,
                    userName:  username,
                    role:      finalRole,
                    adminType: finalRole,
                }));

                // ── Step 7: Navigate to first permitted page ──
                setTimeout(() => {
                    setSuccessMsg("");
                    const firstPage = getFirstPermittedPage(finalRole, result.permissions);
                    console.log("🚀 Navigating to:", firstPage);
                    navigate(firstPage, { replace: true });
                }, 1000);

            } else {
                setErrorMsg(result.message || "Please check the username and password");
                setLoading(false);
            }

        } catch (error: any) {
            console.error("Login error:", error);
            if (error.name === "TypeError" && error.message.includes("fetch")) {
                setErrorMsg("Network error: Unable to connect to server. Please check your connection.");
            } else {
                setErrorMsg(`Login failed: ${error.message}`);
            }
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5", p: 2 }}>
            <Card sx={{ width: 450, p: 4, boxShadow: 4, borderRadius: 2, backgroundColor: "#ffffff" }}>
                <Typography variant="h4" align="center" gutterBottom sx={{ color: "#1976d2", fontWeight: "bold", mb: 3 }}>
                    Admin Login
                </Typography>

                {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
                {errorMsg   && <Alert severity="error"   sx={{ mb: 2 }}>{errorMsg}</Alert>}

                <form onSubmit={handleAdminLogin}>
                    <TextField
                        label="Username"
                        type="text"
                        fullWidth
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        sx={{ mb: 2 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Person color="action" />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your username or email"
                    />

                    <TextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 3 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your password"
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                        sx={{ textTransform: "none", fontWeight: "bold", py: 1.5, fontSize: "1.1rem" }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Signing In...
                            </>
                        ) : (
                            "Sign In"
                        )}
                    </Button>
                </form>
            </Card>
        </Box>
    );
};

// ─────────────────────────────────────────────
// Menu id → route path mapping
// Must match ALL_MENUS paths in SlideMenu.tsx
// ─────────────────────────────────────────────
const MENU_PATH_MAP: Record<string, string> = {
    // Regular Menus
    dashboard:                    "/adminDashboard",
    user_list:                    "/Userlist",
    order_management:             "/adminOrderManagement",
    bulk_order_management:        "/bulkOrderManagement",
    return_process_management:    "/returnprocessManagement",
    ext_warranty_products:        "/extWarrantyProducts",  // Updated from warranty_management
    review:                       "/review",
    guest_order_list:             "/guestOrderlist",
    guest_user_order:             "/guestUserOrder",
    set_credit:                   "/SetCredit",
    pickupPending:                "/pickupPending",
    deal_of_the_day:              "/dealoftheday",
    reward_management:            "/rewardManagement",
    reward_settings_management:   "/RewardSettingsManagement",
    admin_rewards:                "/AdminRewards",
    flash_sale:                   "/flashSale",
    delivery_settings:            "/deliverySettings",
    tax_settings:                 "/taxSettings",
    reports_list:                 "/reportsList",
    user_wallet:                  "/userWallet",
    refer_friend:                 "/referFriend",
    agent_details:                "/agentDetails",        // ✅ NEW
    user_monitor:                 "/userMonitor",         // ✅ NEW

    // Master Menus
    product_management:           "/productManagement",
    bundle_management:            "/bundleManagment",
    category_management:          "/categoryManagement",
    subcategory_management:       "/subcategorymanagement",
    unit_management:              "/unitManagement",
    delivery_agent_management:    "/DeliveryAgentManagement",
    warehouse_management:         "/WarehouseManagement",
    banner_management:            "/banner",
    product_warehouse_management: "/ProductWarehouseManagement",
    delivery_management:          "/deliveryManagement",
    shipping_management:          "/shippingManagement",
    extended_warranty:            "/extendedWarranty",
    career_management:            "/careerManagement",    // ✅ NEW
    membership_plan_management:   "/membershipPlanManagement", // ✅ NEW
    reports_management:           "/reportsManagement",

    // Coupon Menus
    coupon_management:            "/couponManagement",
    admin_coupon_users:           "/AdminCouponUsers",

    // Admin Menus
    admin_user_list:              "/adminUserList",
    role_master:                  "/roleMaster",
    role_mapping:                 "/roleMapping",

    // Global Settings Menus
    smtp_settings:                "/smtpsettings",
    site_settings:                "/siteSettings",        // Renamed from global_settings
    cash_on_delivery:             "/cashOnDelivery",      // ✅ NEW
    about_us:                     "/aboutUsPage",         // ✅ NEW
    services:                     "/servicePage",         // ✅ NEW
};

// ─────────────────────────────────────────────
// Get the first page the user has access to
// after login based on their permissions.
//
// Priority:
//  1. super_admin              → /adminDashboard
//  2. operation / vendor       → /productManagement
//  3. Custom role with perms:
//     - dashboard in perms     → /adminDashboard
//     - else first perm key    → its mapped route
//     - fallback               → /adminDashboard
// ─────────────────────────────────────────────
function getFirstPermittedPage(
    finalRole: string,
    permissions: Record<string, string[]> | null | undefined
): string {
    // Super admin always goes to dashboard (they have all permissions anyway)
    if (finalRole === "super_admin") return "/adminDashboard";

    // If no permissions, fallback to dashboard
    if (!permissions || Object.keys(permissions).length === 0) return "/adminDashboard";

    const permKeys = Object.keys(permissions);

    // Prefer dashboard if permitted
    if (permKeys.includes("dashboard")) return "/adminDashboard";

    // Go to first permitted page in API order
    for (const key of permKeys) {
        const path = MENU_PATH_MAP[key];
        if (path) return path;
    }

    return "/adminDashboard";
}

// ─────────────────────────────────────────────
// Normalize any role string to known types.
// Any unknown/custom role (e.g. "Test", "new")
// → treated as "admin" with dynamic permissions.
// ─────────────────────────────────────────────
function normalizeRole(role: string): string {
    if (!role)                  return "admin";
    if (role.includes("super")) return "super_admin";
    if (role === "operation")   return "operation";
    if (role === "vendor")      return "vendor";
    if (role === "admin")       return "admin";
    // Any other role (e.g. "Test", "new", "manager") → admin with API permissions
    return "admin";
}

export default AdminLogin;